import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles, MessagesSquare, Brain, PanelRightOpen, Loader2, Zap, MoveHorizontal, AlignLeft, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { RsvpStage } from "@/components/reader/RsvpStage";
import { TickerStage } from "@/components/reader/TickerStage";
import { FocusStage } from "@/components/reader/FocusStage";
import { GuidedStage } from "@/components/reader/GuidedStage";
import { useHighlights } from "@/components/reader/useHighlights";
import { SummariesPanel } from "@/components/reader/SummariesPanel";
import { ChatPanel } from "@/components/reader/ChatPanel";
import { KnowledgePanel } from "@/components/reader/KnowledgePanel";
import { CompletionDialog } from "@/components/reader/CompletionDialog";
import { docsApi, analyticsApi } from "@/lib/apiClient";
import { ExplainCard } from "@/components/reader/ExplainCard";
import { useTranslation } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const PREFS_KEY = "reading_os_prefs";

// Reading styles requested by Marina — RSVP (existing) + Ticker (moving text) + Focus.
const READING_MODES = [
    { id: "rsvp", icon: Zap },
    { id: "ticker", icon: MoveHorizontal },
    { id: "focus", icon: AlignLeft },
    { id: "guided", icon: Wand2 },
];

const loadPrefs = () => {
    try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") || {}; }
    catch (e) { return {}; }
};
const savePref = (patch) => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ ...loadPrefs(), ...patch })); } catch (e) {}
};
const prefWpm = () => loadPrefs().wpm || 350;
const prefMode = () => {
    const m = loadPrefs().readingMode;
    return READING_MODES.some((x) => x.id === m) ? m : "rsvp";
};

const PanelTabs = ({ docId, onJumpToWord, value, onValueChange, chatSeed }) => {
    const { t } = useTranslation();
    return (
    <Tabs value={value} onValueChange={onValueChange} className="flex h-full flex-col">
        <div className="px-3 pt-3">
            <TabsList className="grid w-full grid-cols-3 bg-surface-2">
                <TabsTrigger data-testid="tab-summaries" value="summaries" className="gap-1.5 text-xs"><Sparkles className="h-3.5 w-3.5" /> {t("panels.summaries")}</TabsTrigger>
                <TabsTrigger data-testid="tab-chat" value="chat" className="gap-1.5 text-xs"><MessagesSquare className="h-3.5 w-3.5" /> {t("panels.chat")}</TabsTrigger>
                <TabsTrigger data-testid="tab-knowledge" value="knowledge" className="gap-1.5 text-xs"><Brain className="h-3.5 w-3.5" /> {t("panels.knowledge")}</TabsTrigger>
            </TabsList>
        </div>
        <TabsContent value="summaries" className="mt-2 min-h-0 flex-1 overflow-hidden"><SummariesPanel docId={docId} /></TabsContent>
        <TabsContent value="chat" className="mt-2 min-h-0 flex-1 overflow-hidden"><ChatPanel docId={docId} onJumpToWord={onJumpToWord} seed={chatSeed} /></TabsContent>
        <TabsContent value="knowledge" className="mt-2 min-h-0 flex-1 overflow-hidden"><KnowledgePanel docId={docId} /></TabsContent>
    </Tabs>
    );
};

const ModeSwitcher = ({ mode, onChange }) => {
    const { t } = useTranslation();
    return (
    <div className="flex items-center rounded-lg bg-surface-2 p-0.5" data-testid="reading-mode-switcher" role="tablist" aria-label="Reading mode">
        {READING_MODES.map((m) => {
            const Icon = m.icon;
            const activeMode = mode === m.id;
            return (
                <button
                    key={m.id}
                    data-testid={`reading-mode-${m.id}`}
                    role="tab"
                    aria-selected={activeMode}
                    title={t(`reader.modes.${m.id}.hint`)}
                    onClick={() => onChange(m.id)}
                    className={cn(
                        "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs transition-colors sm:px-3",
                        activeMode ? "bg-surface-3 text-t1 shadow-elev-1" : "text-t3 hover:text-t1"
                    )}
                >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{t(`reader.modes.${m.id}.label`)}</span>
                </button>
            );
        })}
    </div>
    );
};

export default function Reader() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [readingMode, setReadingMode] = useState(prefMode); // rsvp | flow | focus
    const [currentIndex, setCurrentIndex] = useState(0);
    const [seekTo, setSeekTo] = useState(null);
    const [activeTab, setActiveTab] = useState("summaries");
    const [sheetOpen, setSheetOpen] = useState(false);
    const [completion, setCompletion] = useState({ open: false, stats: null });
    const completedRef = useRef(false);
    const sessionRef = useRef({ words: 0, seconds: 0 });
    const [explain, setExplain] = useState({ open: false, term: "", context: "", rect: null, ts: 0 });
    const [chatSeed, setChatSeed] = useState(null);
    const [seedTarget, setSeedTarget] = useState("aside");
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const d = await docsApi.get(id);
                setDoc(d);
                completedRef.current = d?.progress?.completed || false;
                setCurrentIndex(d?.progress?.word_index || 0);
            } catch (e) {
                toast.error(t("reader.loadFailed"));
                navigate("/library");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, navigate]);

    const tokens = useMemo(() => (doc?.text ? doc.text.split(/\s+/).filter(Boolean) : []), [doc]);
    const sections = doc?.sections || [];

    // AI Smart Highlights — shared across the modes that show the full text.
    const highlights = useHighlights(id, { active: readingMode === "guided" || readingMode === "focus" });

    const saveProgress = useCallback((index, percent) => {
        setCurrentIndex(index);
        docsApi.updateProgress(id, { word_index: index, percent, completed: percent >= 99.5 }).catch(() => {});
    }, [id]);

    // Mode is supplied by the stage itself so a session flushed during a mode
    // switch is attributed to the correct reading style.
    const onSession = useCallback(({ wordsRead, durationSec, wpm, mode }) => {
        sessionRef.current.words += wordsRead;
        sessionRef.current.seconds += durationSec;
        analyticsApi.event({
            document_id: id,
            type: "reading_session",
            mode: mode || "rsvp",
            wpm,
            words_read: wordsRead,
            duration_sec: durationSec,
        }).catch(() => {});
    }, [id]);

    const changeMode = useCallback((m) => {
        setReadingMode(m);
        savePref({ readingMode: m });
    }, []);

    const openPanel = useCallback((tab) => {
        setActiveTab(tab);
        // Below lg the panel isn't side-by-side, so surface it via the sheet.
        if (window.innerWidth < 1024) setSheetOpen(true);
    }, []);

    const onComplete = useCallback(() => {
        if (completedRef.current) return;
        completedRef.current = true;
        docsApi.updateProgress(id, { word_index: tokens.length, percent: 100, completed: true }).catch(() => {});
        analyticsApi.event({ document_id: id, type: "completed", words_read: 0, duration_sec: 0 }).catch(() => {});

        const totalWords = tokens.length;
        const wpm = prefWpm();
        const sess = sessionRef.current;
        const effWpm = sess.seconds > 5 ? Math.round((sess.words / sess.seconds) * 60) : wpm;
        const avgReaderWpm = 230;
        const timeSaved = Math.max(0, Math.round(totalWords / avgReaderWpm - totalWords / Math.max(effWpm, 1)));
        setCompletion({ open: true, stats: { totalWords, wpm: effWpm, timeSaved } });
    }, [id, tokens.length]);

    const jumpToWord = useCallback((wordIndex) => {
        setReadingMode("rsvp");
        savePref({ readingMode: "rsvp" });
        setCurrentIndex(wordIndex);
        setSeekTo({ index: wordIndex, ts: Date.now() });
    }, []);

    // Click-to-Explain (highlighted-term click or text selection in Guided/Focus).
    const handleExplain = useCallback((term, context, rect, wordIndex) => {
        setExplain({
            open: true,
            term,
            context: context || "",
            wordIndex: (wordIndex != null && Number.isFinite(wordIndex)) ? wordIndex : null,
            rect: rect ? { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right, width: rect.width, height: rect.height } : null,
            ts: Date.now(),
        });
    }, []);

    const handleAskAI = useCallback((term) => {
        const q = t("explain.askQuestion", { term });
        const mobile = window.innerWidth < 1024;
        setActiveTab("chat");
        setSeedTarget(mobile ? "sheet" : "aside");
        setChatSeed({ text: q, ts: Date.now() });
        if (mobile) setSheetOpen(true);
        setExplain((s) => ({ ...s, open: false }));
    }, [t]);

    const handlePickRelated = useCallback((term) => {
        setExplain((s) => ({ ...s, open: true, term, context: "", wordIndex: null, ts: Date.now() }));
    }, []);

    // Deep-links from My Glossary: ?w=<wordIndex> jumps to the passage; ?ask=<term>
    // opens the Chat tab with a grounded question. Consumed once, then cleared.
    useEffect(() => {
        if (!doc) return;
        const w = searchParams.get("w");
        const ask = searchParams.get("ask");
        if (w === null && !ask) return;
        if (w !== null && Number.isFinite(Number(w))) jumpToWord(Number(w));
        if (ask) handleAskAI(ask);
        setSearchParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc]);

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-surface-0">
                <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" />
            </div>
        );
    }
    if (!doc) return null;

    const sharedProps = {
        initialIndex: currentIndex,
        seekTo,
        onProgress: saveProgress,
        onSession,
        onComplete,
    };

    return (
        <div className="flex min-h-screen flex-col bg-surface-0">
            {/* Top bar */}
            <header className="sticky top-0 z-20 border-b border-border bg-surface-0/85 backdrop-blur-md">
                <div className="flex items-center justify-between gap-3 px-3 py-3 sm:px-5">
                    <div className="flex min-w-0 items-center gap-3">
                        <Button data-testid="reader-back-button" variant="ghost" size="icon" className="text-t2" onClick={() => navigate("/library")}>
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="min-w-0">
                            <h1 className="truncate font-sans-display text-sm font-semibold text-t1 sm:text-base">{doc.title}</h1>
                            <div className="flex items-center gap-2 text-t4">
                                <Badge variant="secondary" className="bg-surface-3 text-[10px] uppercase">{doc.source_type}</Badge>
                                <span className="mono-label">{(doc.word_count || 0).toLocaleString()} {t("reader.words")}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <ModeSwitcher mode={readingMode} onChange={changeMode} />

                        {/* Tablet / mobile AI tools sheet — only when the panel can't sit side-by-side (< lg) */}
                        <div className="lg:hidden">
                            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                                <SheetTrigger asChild>
                                    <Button data-testid="open-ai-panel-button" variant="secondary" size="sm" className="gap-1.5 bg-surface-3/60"><PanelRightOpen className="h-4 w-4" /> {t("reader.aiTools")}</Button>
                                </SheetTrigger>
                                <SheetContent side="right" className="w-full border-border bg-surface-1 p-0 sm:max-w-md">
                                    <SheetTitle className="sr-only">{t("reader.aiTools")}</SheetTitle>
                                    <div className="h-full pt-6"><PanelTabs docId={id} onJumpToWord={jumpToWord} value={activeTab} onValueChange={setActiveTab} chatSeed={seedTarget === "sheet" ? chatSeed : null} /></div>
                                </SheetContent>
                            </Sheet>
                        </div>
                    </div>
                </div>
            </header>

            {/* Workspace — keep Reader + AI panel side-by-side from lg (1024px) up.
                Reader column uses minmax(0,1fr)+min-w-0 so it shrinks BEFORE the
                panel is ever removed, and the panel widens on larger screens. */}
            <div className="mx-auto grid w-full max-w-[1600px] flex-1 gap-4 p-3 sm:p-5 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_400px] 2xl:grid-cols-[minmax(0,1fr)_460px]">
                <div className="min-w-0 overflow-hidden" style={{ height: "calc(100vh - 92px)" }}>
                    {readingMode === "rsvp" && (
                        <RsvpStage tokens={tokens} sections={sections} {...sharedProps} />
                    )}
                    {readingMode === "ticker" && (
                        <TickerStage text={doc.text} total={tokens.length} {...sharedProps} />
                    )}
                    {readingMode === "focus" && (
                        <FocusStage text={doc.text} total={tokens.length} {...sharedProps} highlights={highlights} onExplain={handleExplain} />
                    )}
                    {readingMode === "guided" && (
                        <GuidedStage text={doc.text} total={tokens.length} {...sharedProps} highlights={highlights} onExplain={handleExplain} />
                    )}
                </div>

                {/* Desktop/large AI panel — visible side-by-side from lg up */}
                <aside className="hidden min-h-0 min-w-0 rounded-3xl border border-border bg-surface-1 lg:flex lg:flex-col" style={{ height: "calc(100vh - 92px)", position: "sticky", top: 84 }}>
                    <PanelTabs docId={id} onJumpToWord={jumpToWord} value={activeTab} onValueChange={setActiveTab} chatSeed={seedTarget === "aside" ? chatSeed : null} />
                </aside>
            </div>

            <CompletionDialog
                open={completion.open}
                onOpenChange={(o) => setCompletion((c) => ({ ...c, open: o }))}
                title={doc.title}
                stats={completion.stats}
                onReadSummary={() => { setCompletion((c) => ({ ...c, open: false })); openPanel("summaries"); }}
                onChat={() => { setCompletion((c) => ({ ...c, open: false })); openPanel("chat"); }}
                onStudy={() => navigate(`/study/${id}`)}
                onBackToLibrary={() => navigate("/library")}
            />

            <ExplainCard
                open={explain.open}
                term={explain.term}
                context={explain.context}
                wordIndex={explain.wordIndex}
                rect={explain.rect}
                docId={id}
                onClose={() => setExplain((s) => ({ ...s, open: false }))}
                onAskAI={handleAskAI}
                onPickRelated={handlePickRelated}
            />
        </div>
    );
}
